/**
 * 演员管理器
 * 负责管理演员列表，提供获取、切换收藏等功能
 * @author 杨俊朗
 */
import { useToast } from "@/hooks/use-toast";
import { actorDelete } from "@/services/actor-delete";
import { actorShow } from "@/services/actor-show";
import { actorToggleFavorite } from "@/services/actor-toggle-favorite";
import { actorRename } from "@/services/rename-actor-template";
import { myActorToggleFavorite } from "@/services/user-customize-actor-template-toggle-favorite";
import { videoRetryFailedTemplate } from "@/services/video-retry-failed-template";
import { videoUserActorTemplate } from "@/services/video-user-actor-template";
import { useEffect, useState } from "react";
import { assetsActorManager } from "./assets-actor-manager";

// 导出常量供外部使用
export const ACTOR_CONDITIONS = {
  ALL: "all" as const,
  FAVORITE: "fav" as const,
  MY: "my" as const,
} as const;

export const ACTOR_ORDERS = {
  NEWLY_ADDED: "newly_added" as const,
  NAME_DESC: "name_desc" as const,
  NAME_ASC: "name_asc" as const,
  USE_COUNT: "use_count" as const,
} as const;

export type ActorCondition =
  (typeof ACTOR_CONDITIONS)[keyof typeof ACTOR_CONDITIONS];

export type ActorOrder = (typeof ACTOR_ORDERS)[keyof typeof ACTOR_ORDERS];

// 缓存数据结构
interface CacheData {
  actors: TalkingActor[];
  condition: ActorCondition;
  filters: { [key: string]: string[] };
  order: ActorOrder;
  page: number;
  size: number;
  total: number;
  timestamp: number;
}

// 演员管理器
export class CreationActorsManager {
  private static instance: CreationActorsManager;
  private readonly STORAGE_KEY = "MIUTA_ACTORS";
  private actors: TalkingActor[] = [];
  private condition: ActorCondition = ACTOR_CONDITIONS.ALL;
  private filters: { [key: string]: string[] } = {};
  private order: ActorOrder = ACTOR_ORDERS.USE_COUNT;
  private page: number = 1;
  private size: number = 40;
  private total: number = 0;
  // 简化的缓存 - 直接使用查询条件作为key，完整状态对象作为value
  private cache = new Map<string, CacheData>();
  // 轮询
  private polling: NodeJS.Timeout | null = null;
  // 视频轮询映射
  private generatorPollingMap: { [id: string]: NodeJS.Timeout } = {};

  private constructor() { }

  // 单例模式
  public static getInstance(): CreationActorsManager {
    if (!CreationActorsManager.instance) {
      CreationActorsManager.instance = new CreationActorsManager();
    }
    return CreationActorsManager.instance;
  }

  // 生成缓存键
  private generateCacheKey(
    condition: ActorCondition,
    filters: { [key: string]: string[] },
    order: ActorOrder
  ): string {
    return JSON.stringify({ condition, filters, order });
  }

  // 获取缓存数据
  private getCacheData(
    condition: ActorCondition,
    filters: { [key: string]: string[] },
    order: ActorOrder
  ): CacheData | null {
    const key = this.generateCacheKey(condition, filters, order);
    const cached = this.cache.get(key);

    if (!cached) {
      return null;
    }

    return cached;
  }

  // 设置缓存数据
  private setCacheData(
    condition: ActorCondition,
    filters: { [key: string]: string[] },
    order: ActorOrder,
    actors: TalkingActor[],
    page: number,
    size: number,
    total: number
  ): void {
    const key = this.generateCacheKey(condition, filters, order);
    const cacheData: CacheData = {
      actors: [...actors],
      condition,
      filters: { ...filters },
      order,
      page,
      size,
      total,
      timestamp: Date.now(),
    };

    this.cache.set(key, cacheData);
  }

  public async refresh(condition?: ActorCondition) {
    console.log("refresh data from network", {
      condition: this.condition,
      filters: this.filters,
      order: this.order,
    });
    const data = await actorShow({
      // 资产演员删除后，这里要根据 my 条件刷新数据
      condition: condition || this.condition,
      filters: this.filters,
      order: this.order,
      // 重新请求当前所有数据
      page: 1,
      size: this.page * this.size,
    });
    this.actors = data.actors.map((actor) => ({
      id: actor.id,
      modelId: actor.modelId,
      name: actor.name,
      image: actor.imageUrl,
      video: actor.compressedActorDefaultVideoUrl,
      hdVideo: actor.fullActorDefaultVideoUrl,
      tags: actor.tags,
      isFavorite: actor.isFavorite,
      isMyActor: actor.isMyActor,
      isPro: actor.isPro,
      isHD: actor.isHd,
      generateType: actor.generateType,
      source: actor.source,
      audio: actor.defaultAudio
        ? {
          id: actor.defaultAudio.id,
          voiceTtsId: actor.defaultAudio.voiceTtsId,
          modelName: actor.defaultAudio.modelName,
          name: actor.defaultAudio.name,
          ageGroup: actor.defaultAudio.ageGroup,
          language: actor.defaultAudio.language,
          gender: actor.defaultAudio.gender,
          // emotion: actor.defaultAudio.emotion,
          speed: actor.defaultAudio.speed,
          audioUrl: actor.defaultAudio.audioUrl,
          isStarred: actor.defaultAudio.isStarred,
        }
        : null,
      status: actor.generateStatus,
      isUnusual: actor.isUnusual,
      createdAt: actor.createdAt,
    }));
    // 数据始终以后端返回为准
    this.page = data.currentPage;
    this.total = data.total;
    // 缓存数据
    this.setCacheData(
      condition || this.condition,
      this.filters,
      this.order,
      this.actors,
      this.page,
      this.size,
      this.total
    );
    // 触发轮询
    for (const actor of this.actors) {
      if (typeof actor.status === "number" && actor.status < 2) {
        this.generatorPolling(actor.id);
      }
    }
    this.dispatch();
  }

  public async fetch(append = false) {
    // 校验是否还可以追加数据 不可追加则不应触发请求
    if (append && this.page * this.size > this.total) {
      return;
    }
    if (!append) {
      this.actors = [];
      this.page = 1;
      this.total = 0;
      this.dispatch();
      // 尝试从缓存获取数据
      const cached = this.getCacheData(
        this.condition,
        this.filters,
        this.order
      );
      if (cached) {
        console.log("use cache data", {
          condition: this.condition,
          filters: this.filters,
          order: this.order,
        });

        // 直接使用缓存的完整状态
        this.actors = cached.actors;
        this.condition = cached.condition;
        this.filters = cached.filters;
        this.order = cached.order;
        this.page = cached.page;
        this.size = cached.size;
        this.total = cached.total;
        // 触发轮询
        for (const actor of cached.actors) {
          if (typeof actor.status === "number" && actor.status < 2) {
            this.generatorPolling(actor.id);
          }
        }
        this.dispatch();
        return;
      }
    }

    // 从网络获取数据
    console.log("fetch data from network", {
      condition: this.condition,
      filters: this.filters,
      order: this.order,
    });
    const page = append ? this.page + 1 : this.page;
    const data = await actorShow({
      condition: this.condition,
      filters: this.filters,
      order: this.order,
      page,
      size: this.size,
    });

    const mappedActors = data.actors.map((actor) => ({
      id: actor.id,
      modelId: actor.modelId,
      name: actor.name,
      image: actor.imageUrl,
      video: actor.compressedActorDefaultVideoUrl,
      hdVideo: actor.fullActorDefaultVideoUrl,
      tags: actor.tags,
      isFavorite: actor.isFavorite,
      isMyActor: actor.isMyActor,
      isPro: actor.isPro,
      isHD: actor.isHd,
      generateType: actor.generateType,
      source: actor.source,
      audio: actor.defaultAudio
        ? {
          id: actor.defaultAudio.id,
          voiceTtsId: actor.defaultAudio.voiceTtsId,
          modelName: actor.defaultAudio.modelName,
          name: actor.defaultAudio.name,
          ageGroup: actor.defaultAudio.ageGroup,
          language: actor.defaultAudio.language,
          gender: actor.defaultAudio.gender,
          // emotion: actor.defaultAudio.emotion,
          speed: actor.defaultAudio.speed,
          audioUrl: actor.defaultAudio.audioUrl,
          isStarred: actor.defaultAudio.isStarred,
        }
        : null,
      status: actor.generateStatus,
      isUnusual: actor.isUnusual,
      createdAt: actor.createdAt,
    }));
    if (append) {
      this.actors = [...this.actors, ...mappedActors];
    } else {
      this.actors = mappedActors;
    }
    // 数据始终以后端返回为准
    this.page = data.currentPage;
    this.total = data.total;
    // 缓存数据
    this.setCacheData(
      this.condition,
      this.filters,
      this.order,
      this.actors,
      this.page,
      this.size,
      this.total
    );
    // 触发轮询
    for (const actor of this.actors) {
      if (typeof actor.status === "number" && actor.status < 2) {
        this.generatorPolling(actor.id);
      }
    }
    this.dispatch();
  }

  // 获取当前演员列表
  public get(): TalkingActor[] {
    return this.actors;
  }

  // 切换演员收藏状态
  public async toggleFavorite(actorId: string): Promise<void> {
    const targetActor = this.actors.find((actor) => {
      return actor.id === actorId;
    });
    if (!targetActor) {
      throw new Error(`Actor with id ${actorId} not found`);
    }

    const favorite = targetActor.isFavorite;

    // 更新 actors 数组中对应演员的 isFavorite 状态
    this.actors = this.actors.map((actor) => {
      if (actor.id === actorId) {
        return { ...actor, isFavorite: !favorite };
      }
      return actor;
    });

    // 立即触发更新
    this.dispatch();

    try {
      if (this.condition === ACTOR_CONDITIONS.MY || targetActor.source === 'user_template') {
        // 调用用户定制化演员的接口
        await myActorToggleFavorite(actorId);
      } else {
        // 调用系统模板演员的接口
        await actorToggleFavorite(actorId);
      }
      // 成功后，清除相关缓存（因为收藏状态变化可能影响筛选结果）
      this.clearCache();
    } catch (error) {
      // 回退 actors 数组中的状态
      this.actors = this.actors.map((actor) => {
        if (actor.id === actorId) {
          return { ...actor, isFavorite: favorite };
        }
        return actor;
      });

      // 触发回退后的更新
      this.dispatch();

      // 重新抛出错误供调用方处理
      throw error;
    }
  }

  // condition 设置和获取方法
  public setCondition(condition: ActorCondition): void {
    console.log("setCondition", condition);
    this.condition = condition;
    this.page = 1;
    this.dispatch();
  }

  public getCondition(): ActorCondition {
    return this.condition;
  }

  // filters 设置和获取方法
  public setFilters(filters: { [key: string]: string[] }): void {
    console.log("setFilters", filters);
    this.filters = filters;
    this.page = 1;
    this.dispatch();
  }

  public getFilters(): { [key: string]: string[] } {
    return this.filters;
  }

  // order 设置和获取方法
  public setOrder(order: ActorOrder): void {
    console.log("setOrder", order);
    this.order = order;
    this.page = 1;
    this.dispatch();
  }

  public getOrder(): ActorOrder {
    return this.order;
  }

  // page 获取方法（内部管理，不提供设置方法）
  public getPage(): number {
    return this.page;
  }

  // size 获取方法（内部管理，不提供设置方法）
  public getSize(): number {
    return this.size;
  }

  // total 获取方法（内部管理，不提供设置方法）
  public getTotal(): number {
    return this.total;
  }

  public async remove(actor: Actor) {
    // 乐观更新
    const actors = [...this.actors];
    this.actors = this.actors.filter((item) => actor.id !== item.id);
    this.dispatch();
    try {
      // 删除数据
      await actorDelete(actor.id);
      // 后期可改为插入返回的数据 避免重新刷新列表。
      await this.refresh();
      // 刷新资产演员列表数据
      await assetsActorManager.fetch();
    } catch (error) {
      console.error("Failed to remove actor:", error);
      // 回退更新
      this.actors = actors;
      this.dispatch();
      throw error;
    }
  }

  public async regenerate(actor: Actor) {
    // 乐观更新
    const temp = {
      ...actor,
      status: 0,
    };
    const actors = [...this.actors];
    this.actors = [temp, ...this.actors.filter((item) => actor.id !== item.id)];
    this.dispatch();
    try {
      await videoRetryFailedTemplate(actor.id);
      // 后期可改为插入返回的数据 避免重新刷新列表。
      await this.refresh();
    } catch (error) {
      console.error("Failed to regenerate actor:", error);
      // 回退更新
      this.actors = actors;
      this.dispatch();
      throw error;
    }
  }

  public async rename({ actor, name }: { actor: Actor; name: string }) {
    const actorIndex = this.actors.findIndex((item) => item.id === actor.id);
    const originalName = this.actors[actorIndex].name;
    try {
      // 乐观更新：立即更新本地状态
      this.actors[actorIndex].name = name;
      this.actors = [...this.actors];
      this.dispatch();
      const res = await actorRename({ id: actor.id, name });
      if (res.isDuplicate) {
        throw new Error("Actor name already exists");
      }
      return res;
    } catch (error) {
      console.error("Failed to rename actor:", error);
      // 回退更新
      this.actors[actorIndex].name = originalName;
      this.actors = [...this.actors];
      this.dispatch();
      throw error;
    }
  }

  // 单独轮询 用于获取生成视频结果
  public generatorPolling(id: string): () => void {
    // 已在轮询则清除原轮询函数
    if (this.generatorPollingMap[id]) {
      clearTimeout(this.generatorPollingMap[id]);
    }
    this.generatorPollingMap[id] = setTimeout(async () => {
      try {
        const newResult = await videoUserActorTemplate(id);
        // 同步结果
        const existingResults = this.actors || [];
        const resultIndex = existingResults.findIndex((result) => {
          return result.id === newResult.id;
        });
        if (resultIndex !== -1) {
          // 更新结果列表
          const updatedResults = [...existingResults];
          updatedResults[resultIndex] = {
            ...updatedResults[resultIndex],
            ...newResult,
            image: newResult.imageUrl,
          };
          // 更新数据
          this.actors = updatedResults;
          this.dispatch();
          // 是否需要继续轮询
          newResult.generateStatus < 2
            ? this.generatorPolling(id)
            : this.stopGeneratorPolling(id);
          return;
        }
      } catch {
        // 发生错误仍需继续轮询
        this.generatorPolling(id);
        return;
      }
      // 在列表中找不到相应的数据则停止不必要的轮询
      this.stopGeneratorPolling(id);
    }, 10000);
    return () => this.stopGeneratorPolling(id);
  }

  // 停止单独轮询
  public stopGeneratorPolling(id: string) {
    if (this.generatorPollingMap[id]) {
      clearTimeout(this.generatorPollingMap[id]);
      delete this.generatorPollingMap[id];
    }
  }

  // 停止所有轮询
  public stopAllPolling() {
    for (const id of Object.keys(this.generatorPollingMap)) {
      this.stopGeneratorPolling(id);
    }
  }

  // 重置参数到默认值
  public reset(): void {
    this.actors = [];
    this.condition = ACTOR_CONDITIONS.ALL;
    this.filters = {};
    this.order = ACTOR_ORDERS.USE_COUNT;
    this.page = 1;
    this.size = 40;
    // 停止所有轮询
    this.stopAllPolling();
  }

  // 清除所有缓存
  public clearCache(): void {
    this.cache.clear();
    console.log("actor cache cleared");
  }

  // 4. 监听函数可监听演员列表变化
  public listen(listener: (e: ActorsEvent) => void): () => void {
    window.addEventListener(this.STORAGE_KEY, listener);

    return () => {
      window.removeEventListener(this.STORAGE_KEY, listener);
    };
  }

  // 触发监听回调
  private dispatch(): void {
    if (typeof window !== "undefined") {
      const event = new CustomEvent<{
        condition: ActorCondition;
        filters: { [key: string]: string[] };
        order: ActorOrder;
        total: number;
        actors: TalkingActor[];
      }>(this.STORAGE_KEY, {
        detail: {
          condition: this.condition,
          filters: this.filters,
          order: this.order,
          actors: this.actors,
          total: this.total,
        },
      });
      window.dispatchEvent(event);
    }
  }
}

export const useActorData = () => {
  const [actors, setActors] = useState(creationActorsManager.get());
  const [condition, setCondition] = useState(creationActorsManager.getCondition());
  const [filters, setFilters] = useState(creationActorsManager.getFilters());
  const [order, setOrder] = useState(creationActorsManager.getOrder());
  const [total, setTotal] = useState(creationActorsManager.getTotal());
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  // 监听演员数据变化
  useEffect(() => {
    return creationActorsManager.listen(async (e) => {
      setActors(e.detail.actors);
      setCondition(e.detail.condition);
      setFilters(e.detail.filters);
      setOrder(e.detail.order);
      setTotal(e.detail.total);
    });
  }, []);

  useEffect(() => {
    fetch();
    return () => {
      creationActorsManager.reset();
    };
  }, []);

  // 条件切换
  const switchCondition = async (condition: ActorCondition) => {
    creationActorsManager.setCondition(condition);
    await fetch();
  };

  // 筛选切换
  const switchFilters = async (filters: Record<string, string[]>) => {
    creationActorsManager.setFilters(filters);
    await fetch();
  };

  // 排序切换
  const switchOrder = async (order: ActorOrder) => {
    creationActorsManager.setOrder(order);
    await fetch();
  };

  // 重置筛选
  const resetFilters = async () => {
    creationActorsManager.setFilters({});
    await fetch();
  };

  // 获取演员列表
  const fetch = async (loadMore = false) => {
    setLoading(true);
    try {
      await creationActorsManager.fetch(loadMore);
    } catch (error) {
      toast.showToastByCode(error);
    }
    setLoading(false);
  };

  // 加载更多
  const loadMore = async () => {
    if (loading) {
      return;
    }
    if (creationActorsManager.get().length >= total) {
      return;
    }
    await fetch(true);
  };

  // 切换演员收藏状态
  const toggleFavorite = async (actorId: string) => {
    try {
      await creationActorsManager.toggleFavorite(actorId);
    } catch (error) {
      toast.showToastByCode(error);
    }
  };

  return {
    actors,
    condition,
    filters,
    order,
    total,
    loading,
    fetch,
    switchCondition,
    switchFilters,
    switchOrder,
    resetFilters,
    toggleFavorite,
    loadMore,
  };
};

export const useActorRemove = () => {
  const { showToastByCode } = useToast();

  const remove = async (actor: Actor) => {
    try {
      await creationActorsManager.remove(actor);
    } catch (error) {
      showToastByCode(error);
    }
  };

  return {
    remove,
  };
};

export const useActorRegenerate = () => {
  const { showToastByCode } = useToast();

  const regenerate = async (actor: Actor) => {
    try {
      await creationActorsManager.regenerate(actor);
    } catch (error) {
      showToastByCode(error);
    }
  };

  return {
    regenerate,
  };
};

export const useActorRename = () => {
  const { showToastByCode } = useToast();

  const rename = async (actor: Actor, name: string) => {
    try {
      await creationActorsManager.rename({ actor, name });
    } catch (error) {
      showToastByCode(error);
    }
  };

  return {
    rename,
  };
};

export const creationActorHooks = {
  useData: useActorData,
  useRemove: useActorRemove,
  useRegenerate: useActorRegenerate,
  useRename: useActorRename,
};

// 导出单例实例
export const creationActorsManager = CreationActorsManager.getInstance();
